import { createCustomBlock } from '../../../utils/createCustomBlock';
import { t } from '../../../utils/I18nManager';
import { resolvePreviewColor } from '@core/utils';
import { getListing, isListingLoading } from '../../../utils/listingCache';
import { buildListingDataAttr } from '../../../utils/listingDataAttr';
import {
	isListingUrlTag,
	previewListingUrl,
	resolveListingHref,
	stripEmptyListingHrefs,
	toLoopListingHref,
} from '../../../utils/resolveListingHref';
import { BasicType } from '../../../constants';
import { IBlockData, RecursivePartial } from '../../../typings';
import { BasicBlock } from '../../../components/BasicBlock';
import { merge } from 'lodash';
import * as React from 'react';
import * as Handlebars from 'handlebars';
import { listingLayouts } from '../../../data';
import { processHtmlMergeTags } from '../../../utils/getMergeTagImageUrl';
import { getProxiedImageUrl } from '../../../utils/getProxiedImageUrl';
import { getListingPlaceholderImage } from '../../../utils/getImg';
import { registerDynamicTextHandlebarsHelpers } from '../../../utils/dynamicTextFormatHelpers';

// Register the raw helper for Handlebars to enable {{{{raw}}}} syntax
Handlebars.registerHelper('raw', function (options) {
	return options.fn();
});
registerDynamicTextHandlebarsHelpers(Handlebars);

// Register conditional helper for modulo operations (used in double column layouts)
Handlebars.registerHelper(
	'ifCond',
	function (
		this: any,
		v1: any,
		operator: string,
		v2: any,
		comparator: string,
		v3: any,
		options: any,
	) {
		// Handle modulo operation: {{#ifCond @index '%' 2 '===' 0}}
		let result;
		if (operator === '%') {
			const modResult = v1 % v2;
			if (comparator === '===') {
				result = modResult === v3;
			} else if (comparator === '==') {
				result = modResult == v3;
			} else if (comparator === '!==') {
				result = modResult !== v3;
			} else if (comparator === '!=') {
				result = modResult != v3;
			}
		}

		if (result) {
			return options.fn(this);
		}
		return options.inverse(this);
	},
);

// Define the custom block type locally to avoid circular dependencies
const CustomBlockType = {
	LISTINGS_BLOCK: 'listings_block' as const,
};

interface ListingItem {
	id: string;
	address?: string;
	streetaddress?: string;
	city?: string;
	stateProvince?: string;
	state?: string;
	postalCode?: string;
	zip_code?: string;
	list_price?: number | string;
	beds?: number;
	baths?: number;
	sq_foot?: number;
	image_data?: string[];
	url?: string;
	listingurl?: string;
}

interface IListingsBlockData {
	/** The bindings. This is the ONLY listing state that gets persisted. */
	selectedListingIds?: string[];
	/**
	 * @deprecated Resolved listing values are no longer saved — a stored copy
	 * goes stale the moment a listing changes price, status, or photos. Read at
	 * load time to recover ids from pre-migration designs, then dropped.
	 */
	selectedListings?: ListingItem[];
	maxCount: number;
}

/**
 * Designs saved before bindings were reference-only carry whole listing objects.
 * Recover the ids from whichever field has them.
 */
export function getListingsBlockIds(
	value?: IListingsBlockData | null,
): string[] {
	if (value?.selectedListingIds?.length) return value.selectedListingIds;

	return (value?.selectedListings || [])
		.map(listing => listing?.id)
		.filter((id): id is string => Boolean(id));
}

interface IListingsBlockAttributes {
	'background-color': string;
	'button-text-color': string;
	'button-color': string;
	'listing-name-color': string;
	'listing-price-color': string;
	'listing-address-color': string;
	'title-font-size': string;
	'price-font-size': string;
	'address-font-size': string;
	'button-font-size': string;
	'button-text': string;
	'show-title': string; // 'true' or 'false'
	'show-address': string; // 'true' or 'false'
	'show-price': string; // 'true' or 'false'
	'show-details': string; // 'true' or 'false'
	layout: 'single' | 'double' | 'table';
	padding: string;
	title: string;
	[key: string]: string;
}

export type IListingsBlock = IBlockData<IListingsBlockAttributes, IListingsBlockData>;

export const ListingsBlock = createCustomBlock<IListingsBlock>({
	get name() {
		return t('Multiple');
	},
	type: CustomBlockType.LISTINGS_BLOCK,
	validParentType: [BasicType.PAGE, BasicType.WRAPPER, BasicType.SECTION],
	create: (payload?: RecursivePartial<IListingsBlock>) => {
		const defaultData: IListingsBlock = {
			type: CustomBlockType.LISTINGS_BLOCK,
			data: {
				value: {
					selectedListingIds: [],
					maxCount: 4,
				},
			},
			attributes: {
				'background-color': 'Transparent',
				'button-text-color': '#ffffff',
				'button-color': '#000000',
				'listing-name-color': '#000000',
				'listing-price-color': '#000000',
				'listing-address-color': '#666666',
				'title-font-size': '16px',
				'price-font-size': '18px',
				'address-font-size': '14px',
				'button-font-size': '14px',
				'button-text': 'View Listing',
				'show-title': true,
				'show-address': true,
				'show-price': true,
				'show-details': true,
				layout: 'double',
				padding: '0px 0px 0px 0px',
				title: 'Multiple Listings',
				href: '{{listing.url}}',
				target: '_blank',
			} as any,
			children: [],
		};
		return merge(defaultData, payload);
	},
	render: (params: {
		data: IListingsBlock;
		idx?: string | null;
		mode: 'testing' | 'production';
		context?: any;
		dataSource?: { [key: string]: any };
		children?: React.ReactNode;
		keepClassName?: boolean;
		renderPortal?: any;
	}) => {
		const { data } = params;
		let maxCount = data.data.value.maxCount || 4;

		// Determine the actual rendering mode
		// 'edit' = editor canvas (show merge tags, maxCount cards, keep {{{{raw}}}})
		// 'preview' = preview/test modal (show fake data, maxCount cards, strip {{{{raw}}}})
		// 'production' = final export (show merge tags, ONE card, keep {{{{raw}}}})
		let actualMode: 'edit' | 'preview' | 'production';

		if (params.mode === 'production') {
			actualMode = 'production';
		} else if (params.dataSource?.previewMode) {
			// Preview/test modal should show fake data
			actualMode = 'preview';
		} else {
			// Editor canvas shows merge tags
			actualMode = 'edit';
		}

		// Resolve the bindings live. `getListing` is a synchronous cache read that
		// schedules a fetch on a miss; the canvas re-renders when results land, so
		// a saved design never displays listing values frozen at save time.
		const boundListingIds = getListingsBlockIds(data.data.value);
		const resolvedListings = boundListingIds
			.map(id => getListing(id))
			.filter((listing): listing is ListingItem => Boolean(listing));
		const isResolving = boundListingIds.some(id => isListingLoading(id));
		// Ids that resolved to nothing simply drop out — the block renders the
		// listings it does have, and sample data only when it has none.
		const listings: ListingItem[] = resolvedListings.length
			? resolvedListings
			: isResolving
				? []
				: data.data.value.selectedListings || [];
		const selectedLayout = data.attributes.layout || 'double';

		// Process listings with image proxy for all layouts
		// Double layout: adds width, height, and fit parameters for resize (250x167)
		// Other layouts: uses proxy for validation only (no resize parameters)
		const useProxy = selectedLayout === 'double';
		const fallbackUrl = 'https://placehold.co/250x167/e1e1e1/666666?text=No+Image';

		const processedListingsData = listings.map((listing: ListingItem, index: number) => {
			const imageUrl = listing.image_data?.[0];

			// Handle empty/nophoto URLs - a real listing with no photo gets a styled
			// stock placeholder (cycled by index) instead of the plain gray "No Image"
			// box, matching the backend's own fallback for the sent email.
			let validatedUrl: string;
			if (!imageUrl || imageUrl.trim() === '' || imageUrl.includes('nophoto')) {
				validatedUrl = getProxiedImageUrl(
					getListingPlaceholderImage(`placeholder_${(index % 8) + 1}`),
					useProxy ? '250' : undefined,
					useProxy ? '167' : undefined,
					fallbackUrl,
				);
			} else {
				// Use proxy with appropriate resize parameters based on layout
				validatedUrl = getProxiedImageUrl(
					imageUrl,
					useProxy ? '250' : undefined,
					useProxy ? '167' : undefined,
					fallbackUrl,
				);
			}

			return {
				...listing,
				image_data: validatedUrl ? [validatedUrl] : [],
			};
		});

		const limitedListings = processedListingsData.slice(0, maxCount);

		// Get isDesignAdmin from dataSource for conditional rendering
		const isDesignAdmin = params.dataSource?.isDesignAdmin;

		// Determine what to display based on mode
		let displayListings;

		if (actualMode === 'production') {
			// PRODUCTION: One element only (backend will loop with {{#each}})
			if (limitedListings.length > 0) {
				displayListings = [limitedListings[0]];
			} else {
				displayListings = [
					{
						id: 'sample-1',
						address: '12345-0 Sample St.',
						city: 'Anytowne',
						stateProvince: 'CA',
						postalCode: '90210',
						list_price: 499000,
						beds: 3,
						baths: 2,
						sq_foot: 1800,
						image_data: ['https://placehold.co/300x200/e1e1e1/666666?text=Sample'],
					},
				];
			}
		} else {
			// EDIT or PREVIEW: Show maxCount cards
			if (limitedListings.length > 0) {
				displayListings = limitedListings;
			} else {
				// Generate maxCount sample listings
				displayListings = Array.from({ length: maxCount }, (_, index) => ({
					id: `sample-${index + 1}`,
					address: `${12345 + '-' + index * 10} Sample St.`,
					city: 'Anytowne',
					stateProvince: 'CA',
					postalCode: '90210',
					list_price: 499000 + index * 50000,
					beds: 3 + (index % 2),
					baths: 2 + (index % 3),
					sq_foot: 1800 + index * 100,
					image_data: [
						getProxiedImageUrl(
							getListingPlaceholderImage(`placeholder_${(index % 8) + 1}`),
							useProxy ? '250' : undefined,
							useProxy ? '167' : undefined,
							fallbackUrl,
						),
					],
				}));
			}
		}

		// Find the layout configuration
		const layoutConfig = listingLayouts.find(
			(layout: any) => layout.id === selectedLayout,
		);
		if (!layoutConfig) {
			console.error(`Layout "${selectedLayout}" not found`);
			return (
				<BasicBlock
					params={params}
					tag='mj-text'
				>
					<div>Layout configuration not found</div>
				</BasicBlock>
			);
		}

		// Prepare handlebars data based on actual mode
		const processedListings = displayListings.map((listing, i) => {
			const formattedPrice =
				typeof listing.list_price === 'number'
					? '$' + listing.list_price.toLocaleString()
					: listing.list_price
						? '$' + listing.list_price
						: '$0';

			if ((actualMode === 'edit' && isDesignAdmin) || actualMode === 'production') {
				// SEND-TIME SEAM — see docs/LISTING_BINDING_CONTRACT.md
				//
				// Production emits ONE card of bare {{address}}/{{listPrice}} tags for
				// the backend to loop with {{#each}}. Which listings it loops over is
				// not in the HTML — the bindings (`selectedListingIds`) ride in the
				// block JSON, and the send pipeline is expected to read them out of
				// etc.json.content and resolve at dispatch.
				//
				// TODO(vignesh): confirm the pipeline resolves from those ids and not
				// from the deprecated `selectedListings` objects, which are no longer
				// written. If it reads the objects, a re-saved design sends an empty
				// listing loop.
				//
				// In DSA EDIT or PRODUCTION: Return field names (will become merge tags via {{{{raw}}}})
				return {
					imagePath:
						actualMode === 'production'
							? 'imagePath'
							: getProxiedImageUrl(
									getListingPlaceholderImage(`placeholder_${(i % 8) + 1}`),
									useProxy ? '250' : undefined,
									useProxy ? '167' : undefined,
									fallbackUrl,
								),
					address: 'address',
					city: 'city',
					stateProvince: 'stateProvince',
					postalCode: 'postalCode',
					listPrice: 'listPrice',
					beds: 'beds',
					baths: 'baths',
					sqFoot: 'sqFoot',
					url: 'url',
				};
			} else {
				// PREVIEW or CBAY Edit: Return actual data for realistic preview
				const listingContext = {
					imagePath:
						listing.image_data &&
						Array.isArray(listing.image_data) &&
						listing.image_data.length > 0
							? listing.image_data[0]
							: 'https://placehold.co/300x200/e1e1e1/666666?text=No+Image',
					// address/stateProvince/postalCode aren't real columns on the raw backend
					// row - the picker translates them from streetaddress/state/zip_code at
					// selection time, but that translation doesn't survive a reload, where
					// this resolves straight off the raw row (see resolveAttachedListingMergeTags.ts's
					// resolveByPath and dataFields.js's listing.address/listing.zip entries for
					// the same pattern). Fall back to the raw columns so a reopened design
					// still shows the full address, not just city.
					address: listing.address || listing.streetaddress || '',
					city: listing.city || '',
					stateProvince: listing.stateProvince || listing.state || '',
					postalCode: listing.postalCode || listing.zip_code || '',
					listPrice: formattedPrice,
					beds: listing.beds || 0,
					baths: listing.baths || 0,
					sqFoot: listing.sq_foot ? listing.sq_foot.toLocaleString() : '0',
					// listingurl is the composed absolute link; the raw url column is a
					// relative path only (see resolveAttachedListingMergeTags.ts's
					// resolveByPath - listingurl wins there too when it has a value).
					url: listing.listingurl || listing.url || 'https://example.com/listing',
				};

				return {
					...listingContext,
					previewHref: previewListingUrl(listing.listingurl || listing.url, params.dataSource?.user?.officeDomain),
					listing: { ...listingContext },
					user: params.dataSource?.user || {},
					contact: params.dataSource?.contact || {},
				};
			}
		});

		// Generate required CSS classes for block selection
		const isTest = params.mode === 'testing';
		let cssClasses = 'email-block';
		if (isTest && params.idx) {
			cssClasses = `email-block node-idx-${params.idx} node-type-${data.type}`;
		}

		// Resolve dynamic brand-colour merge tags to real hex in preview/testing mode;
		// in production the raw tag is returned unchanged for send-time resolution
		// (the production regex path below reads these resolved values).
		const resolveColor = (key: string, fallback: string) =>
			resolvePreviewColor(data.attributes[key], params.mode, params.dataSource) ||
			fallback;

		// Prepare template context
		const templateContext: {
			cssClasses: string;
			listingRefAttr: string;
			backgroundColor: string;
			buttonTextColor: string;
			buttonColor: string;
			listingNameColor: string;
			listingPriceColor: string;
			listingAddressColor: string;
			titleFontSize: string;
			priceFontSize: string;
			addressFontSize: string;
			buttonFontSize: string;
			buttonText: string;
			showTitle: boolean;
			showAddress: boolean;
			showPrice: boolean;
			showDetails: boolean;
			padding: string;
			title: string;
			href: string;
			listings: any[];
			hasListings: boolean;
			listingCount: number;
		} = {
			// Essential CSS classes for block selection
			cssClasses,
			// Production-only: the real binding spliced onto the root <table> itself,
			// same treatment Image/index.tsx gives data-dynamic-tag on the <img>.
			listingRefAttr:
				actualMode === 'production' ? buildListingDataAttr({ ids: boundListingIds }) : '',
			// Block attributes mapped to handlebars context
			backgroundColor: resolveColor('background-color', 'Transparent').toLowerCase(),
			buttonTextColor: resolveColor('button-text-color', '#ffffff'),
			buttonColor: resolveColor('button-color', '#000000'),
			listingNameColor: resolveColor('listing-name-color', '#000000'),
			listingPriceColor: resolveColor('listing-price-color', '#000000'),
			listingAddressColor: resolveColor('listing-address-color', '#666666'),
			titleFontSize: data.attributes['title-font-size'] || '16px',
			priceFontSize: data.attributes['price-font-size'] || '18px',
			addressFontSize: data.attributes['address-font-size'] || '14px',
			buttonFontSize: data.attributes['button-font-size'] || '14px',
			buttonText: data.attributes['button-text'] || 'View Listing',
			href: resolveListingHref(
				toLoopListingHref(data.attributes['href'] as string),
				actualMode,
				'{{url}}',
			),
			showTitle:
				(data.attributes['show-title'] as any) !== false &&
				data.attributes['show-title'] !== 'false',
			showAddress:
				(data.attributes['show-address'] as any) !== false &&
				data.attributes['show-address'] !== 'false',
			showPrice:
				(data.attributes['show-price'] as any) !== false &&
				data.attributes['show-price'] !== 'false',
			showDetails:
				(data.attributes['show-details'] as any) !== false &&
				data.attributes['show-details'] !== 'false',
			padding: data.attributes.padding || '20px',

			// Template content
			title: data.attributes.title || 'Multiple Listings',

			// Listings data
			listings: processedListings,
			hasListings: processedListings.length > 0,
			listingCount: processedListings.length,
		};

		try {
			let htmlContent: string;
			let templateString = layoutConfig.data;
			// The raster service renders at a mobile-width viewport, so the layout's
			// @media rules would stack the cards; the thumbnail keeps the desktop
			// columns like the canvas (monday 13121531553).
			if (params.dataSource?.rasterMode) {
				templateString = templateString.replace(/<style\b[^>]*>[\s\S]*?<\/style>/gi, '');
			}

			if (actualMode === 'production') {
				// PRODUCTION MODE: Build HTML manually preserving {{#each listings}} for backend
				// Extract the card template from inside the {{#each}} loop
				const eachMatch = templateString.match(
					/\{\{#each listings\}\}(.*?)\{\{\/each\}\}/s,
				);
				if (!eachMatch) {
					throw new Error('Could not find {{#each listings}} block in template');
				}

				const cardTemplate = eachMatch[1];
				const wrapperBefore = templateString.substring(
					0,
					templateString.indexOf('{{#each listings}}'),
				);
				const wrapperAfter = templateString.substring(
					templateString.indexOf('{{/each}}') + '{{/each}}'.length,
				);

				// Replace style variables in wrapper
				const processedWrapperBefore = wrapperBefore
					.replace(/\{\{cssClasses\}\}/g, templateContext.cssClasses)
					.replace(/\{\{\{listingRefAttr\}\}\}/g, templateContext.listingRefAttr)
					.replace(/\{\{backgroundColor\}\}/g, templateContext.backgroundColor)
					.replace(/\{\{padding\}\}/g, templateContext.padding);

				// Process card template - replace parent variables but keep {{{{raw}}}} intact
				let processedCard = cardTemplate
					.replace(/\{\{\.\.\/href\}\}/g, templateContext.href)
					.replace(/\{\{\.\.\/buttonColor\}\}/g, templateContext.buttonColor)
					.replace(/\{\{\.\.\/buttonTextColor\}\}/g, templateContext.buttonTextColor)
					.replace(/\{\{\.\.\/listingNameColor\}\}/g, templateContext.listingNameColor)
					.replace(/\{\{\.\.\/listingPriceColor\}\}/g, templateContext.listingPriceColor)
					.replace(
						/\{\{\.\.\/listingAddressColor\}\}/g,
						templateContext.listingAddressColor,
					)
					.replace(/\{\{\.\.\/titleFontSize\}\}/g, templateContext.titleFontSize)
					.replace(/\{\{\.\.\/priceFontSize\}\}/g, templateContext.priceFontSize)
					.replace(/\{\{\.\.\/addressFontSize\}\}/g, templateContext.addressFontSize)
					.replace(/\{\{\.\.\/buttonFontSize\}\}/g, templateContext.buttonFontSize)
					.replace(/\{\{\.\.\/buttonText\}\}/g, templateContext.buttonText);

				// Handle conditionals
				processedCard = processedCard.replace(
					/\{\{#if \.\.\/showAddress\}\}(.*?)\{\{\/if\}\}/gs,
					templateContext.showAddress ? '$1' : '',
				);
				processedCard = processedCard.replace(
					/\{\{#if \.\.\/showPrice\}\}(.*?)\{\{\/if\}\}/gs,
					templateContext.showPrice ? '$1' : '',
				);
				processedCard = processedCard.replace(
					/\{\{#if \.\.\/showDetails\}\}(.*?)\{\{\/if\}\}/gs,
					templateContext.showDetails ? '$1' : '',
				);
				// buttonText/buttonTextColor were already substituted into both branches
				// above; templateContext always defaults them to a truthy value, so the
				// true branch is always the right one - this just strips the surrounding
				// {{#if}}/{{else}}/{{/if}} markup, which would otherwise survive as
				// literal text in the sent email (e.g. "{{#if ../buttonText}}View
				// Listing{{else}}More Info{{/if}}" instead of just "View Listing").
				processedCard = processedCard.replace(
					/\{\{#if \.\.\/buttonTextColor\}\}(.*?)\{\{else\}\}.*?\{\{\/if\}\}/gs,
					'$1',
				);
				processedCard = processedCard.replace(
					/\{\{#if \.\.\/buttonText\}\}(.*?)\{\{else\}\}.*?\{\{\/if\}\}/gs,
					'$1',
				);

				// Strip {{{{raw}}}} wrappers, leaving merge tags
				processedCard = processedCard.replace(
					/\{\{\{\{raw\}\}\}\}(.*?)\{\{\{\{\/raw\}\}\}\}/gs,
					'$1',
				);

				// Reassemble with {{#each listings}} preserved
				htmlContent =
					processedWrapperBefore +
					'{{#each listings}}' +
					processedCard +
					'{{/each}}' +
					wrapperAfter;
			} else {
				// EDIT or PREVIEW MODE: Use Handlebars compilation
				// Preview links each card to its own listing when Link Address is the listing
				// URL field; sample cards get '' and are unlinked (monday 13122054004).
				if (
					actualMode === 'preview' &&
					isListingUrlTag(toLoopListingHref(data.attributes['href'] as string) || '{{url}}')
				) {
					templateString = templateString.replace(/\{\{\.\.\/href\}\}/g, '{{previewHref}}');
				}

				if (actualMode === 'edit') {
					// Always expose {{imagePath}} for Handlebars evaluation — the URL is always
					// resolved to an actual value in templateContext, so the raw wrapper is not needed.
					templateString = templateString.replace(
						/\{\{\{\{raw\}\}\}\}(\{\{imagePath\}\})\{\{\{\{\/raw\}\}\}\}/g,
						'$1',
					);
				}

				/// In PREVIEW mode and CBAY edit mode, strip {{{{raw}}}} blocks
				if (actualMode === 'preview' || (actualMode === 'edit' && !isDesignAdmin)) {
					templateString = templateString.replace(/\{\{\{\{raw\}\}\}\}/g, '');
					templateString = templateString.replace(/\{\{\{\{\/raw\}\}\}\}/g, '');
				}

				// Compile and render - this EXECUTES {{#each listings}} and produces multiple cards
				const template = Handlebars.compile(templateString);
				htmlContent = template(templateContext);

				// In testing mode, process HTML to replace merge tag image URLs with placeholders
				if (params.mode === 'testing') {
					htmlContent = processHtmlMergeTags(htmlContent, params.mode);
				}
				// Canvas/preview cards must not be links (monday 13065458913).
				htmlContent = stripEmptyListingHrefs(htmlContent);
			}

			// The {{{{raw}}}} blocks in EDIT/PRODUCTION modes preserve merge tags for downstream processing

			// NOTE: Only raw, do not use other tags here
			const mjmlContent = `
				<mj-raw>
					${htmlContent}
				</mj-raw>
      `;

			return (
				<BasicBlock
					params={params}
					tag='mj-section'
				>
					{mjmlContent}
				</BasicBlock>
			);
		} catch (error) {
			console.error('Error rendering listing block template:', error);
			console.error('Error details:', {
				actualMode,
				selectedLayout,
				listingCount: processedListings.length,
				hasLayoutConfig: !!layoutConfig,
				errorMessage: error instanceof Error ? error.message : String(error),
				errorStack: error instanceof Error ? error.stack : undefined,
			});
			return (
				<BasicBlock
					params={params}
					tag='mj-text'
				>
					<div>
						Error rendering multiple listings:{' '}
						{error instanceof Error ? error.message : String(error)}
					</div>
				</BasicBlock>
			);
		}
	},
});
